// src/components/RichiestaFormazione.js
// ═══════════════════════════════════════════════════════════════════════════
// «🎓 RICHIEDI FORMAZIONE» (W426) → PRENOTAZIONE DAL CALENDARIO (W427, regole 68)
// ═══════════════════════════════════════════════════════════════════════════
// Formazione = servizio accessorio, su prenotazione e secondo disponibilità (prezzi in
// utils/pianiNexiora.js → FORMAZIONE). Titolare e amministratori scelgono la formula e:
//  • se il Platform Owner ha inserito fasce libere adatte, scelgono giorno e orario
//    (Cloud Function prenotaFormazione: controlla orario, avvio unico per azienda, ruolo);
//  • se non ci sono date, o nessuna va bene, lasciano una richiesta con le date possibili
//    (raccolta «richiesteFormazione»; l'avvio passa dalla funzione, per restare unico).
// Regole di calendario in utils/formazioneCalendario.js (stesse della Cloud Function).
// In alto «Le vostre prenotazioni», con il link della video call e «Annulla» fino a 48 ore prima.
// W429: il Platform Owner che visita un'azienda apre la stessa finestra in ANTEPRIMA (dati veri, senza prenotare).
//
// Si apre da: La mia area, Impostazioni → Organizzazione, Utenti (riquadro del piano), il
// suggerimento di Nexi. Tutti mandano l'evento EVENTO_FORMAZIONE; la finestra è montata una
// volta in App.js (FinestraFormazione).
// ═══════════════════════════════════════════════════════════════════════════
import React, { useEffect, useMemo, useState } from "react";
import { addDoc, collection, doc, onSnapshot, query, serverTimestamp, where } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { db } from "../firebase";
import { useAuth } from "../contexts/AuthContext";
import { useOrg } from "../contexts/OrgContext";
import { useToast } from "../utils/Toast";
import { confirm } from "../utils/confirm";
import { FORMAZIONE } from "../utils/pianiNexiora";
import { A_PAGAMENTO, REGOLE_CALENDARIO, NOMI_FORMULE, adessoRoma, avvioNelPeriodo, fasceDisponibili, fineIncontro, minutiAlInizio, precedentiAzienda, testoAvvioUsato, testoPrecedente } from "../utils/formazioneCalendario";

// prezzi della formazione in euro interi, come sul sito (€450, non €450,00)
const euro = (n) => `€${Number(n).toLocaleString("it-IT")}`;

export const EVENTO_FORMAZIONE = "nexiora:richiedi-formazione";
/** Apre la finestra da qualunque punto dell'app. */
export const apriRichiestaFormazione = (formula = "") => {
  try { window.dispatchEvent(new CustomEvent(EVENTO_FORMAZIONE, { detail: { formula } })); } catch { /* browser molto vecchio */ }
};

const F = FORMAZIONE;
export const FORMULE = [
  { id: "avvio", label: `Avvio gratuito (${F.avvioGratuitoMinuti} minuti in video call)`, prezzo: "incluso", nota: `Durante la prova, una volta per azienda, entro i primi ${F.avvioEntroGiorniProva} giorni.` },
  { id: "videocall", label: `Pacchetto in video call (${F.videocall.ore} ore)`, prezzo: euro(F.videocall.prezzo), nota: `Fino a ${F.maxPartecipanti} partecipanti, con la registrazione.` },
  { id: "presenza", label: `Pacchetto in presenza (${F.presenza.ore} ore)`, prezzo: `${euro(F.presenza.prezzo)} + trasferta`, nota: `Nella vostra sede, fino a ${F.maxPartecipanti} partecipanti. Riceverete il preventivo con la trasferta.` },
  { id: "ora", label: "Ora singola in video call", prezzo: euro(F.oraSingola), nota: "Per un dubbio preciso o una funzione nuova." },
];

/** Avvio gratuito disponibile per il periodo? (prova in corso, entro i primi giorni) */
export const avvioDisponibile = (org, adesso = new Date()) => avvioNelPeriodo(org, adesso);

/** «martedì 3 novembre» da "2026-11-03" */
export const giornoEsteso = (s) => { const [a, m, g] = String(s).split("-").map(Number); return new Date(a, m - 1, g).toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" }); };
const STATI = { confermata: { t: "Confermata", c: "#047857", bg: "#ecfdf5" }, da_confermare: { t: "In attesa di conferma", c: "#92400e", bg: "#fffbeb" } };

/** Pulsante da mettere dove serve (solo titolare e amministratori). */
export function PulsanteFormazione({ stile = {}, testo = "🎓 Richiedi formazione" }) {
  const { isOrgAdmin, isPlatformOwner } = useAuth();
  const { effectiveOrgId } = useOrg();
  // W429: il Platform Owner che visita un'azienda vede l'anteprima (stessa finestra, senza prenotare)
  const anteprima = isPlatformOwner && !!effectiveOrgId;
  if ((!isOrgAdmin || isPlatformOwner) && !anteprima) return null;
  const etichetta = anteprima ? `${testo} (anteprima)` : testo;
  return (
    <button type="button" onClick={() => apriRichiestaFormazione()}
      style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 14px", borderRadius: 9, border: "1px solid #ddd6fe", background: "#faf5ff",
        color: "#6d28d9", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "'Inter',sans-serif", ...stile }}>
      {etichetta}
    </button>
  );
}

const chiama = (nome, dati) => httpsCallable(getFunctions(), nome)(dati);
const messaggioDi = (e) => (e?.details?.codice || /failed-precondition/.test(e?.code || "")) && e?.message ? e.message : "Operazione non riuscita. Riprova, oppure scrivi a info@nexiora.it.";

/** La finestra, montata una volta in App.js. */
export function FinestraFormazione() {
  const { currentUser, userProfile, isOrgAdmin, isPlatformOwner } = useAuth();
  const { effectiveOrgId: orgId, orgVisitata: org } = useOrg();
  const { addToast } = useToast();
  const [aperta, setAperta] = useState(false);
  const [formula, setFormula] = useState("");
  const [oreAggiuntive, setOreAggiuntive] = useState(0);
  const [partecipanti, setPartecipanti] = useState(2);
  const [date, setDate] = useState("");
  const [sede, setSede] = useState("");
  const [telefono, setTelefono] = useState("");
  const [note, setNote] = useState("");
  const [invio, setInvio] = useState(false);
  const [esito, setEsito] = useState(null);        // { tipo: "prenotata" | "richiesta", stato, data, inizio, fine }
  const [errore, setErrore] = useState("");
  const [scelta, setScelta] = useState(null);      // { fasciaId, inizio }
  const [modoRichiesta, setModoRichiesta] = useState(false);
  const [tutteLeDate, setTutteLeDate] = useState(false);
  const [fasce, setFasce] = useState(null);        // null = in caricamento
  const [avvio, setAvvio] = useState(null);        // formazioneAvvii/{orgId}
  const [prenotazioni, setPrenotazioni] = useState([]);
  const [richieste, setRichieste] = useState([]);
  const anteprima = isPlatformOwner && !!orgId;   // W429: vede quello che vede il cliente, non prenota
  const puo = (isOrgAdmin && !isPlatformOwner) || anteprima;
  const uid = currentUser?.uid;

  useEffect(() => {
    const apri = (e) => {
      setFormula(e?.detail?.formula || (avvioDisponibile(org) ? "avvio" : "videocall"));
      setEsito(null); setErrore(""); setScelta(null); setModoRichiesta(false); setTutteLeDate(false); setAperta(true);
      setTelefono(t => t || userProfile?.phone || "");
      setSede(s => s || org?.indirizzo || "");
    };
    window.addEventListener(EVENTO_FORMAZIONE, apri);
    return () => window.removeEventListener(EVENTO_FORMAZIONE, apri);
  }, [org, userProfile?.phone]);

  // Calendario, avvio dell'azienda, prenotazioni e richieste dei colleghi (solo a finestra aperta).
  // Se una lettura non riesce, la finestra resta usabile: si lascia la richiesta, come in W426.
  const attivo = aperta && puo && !!orgId;
  useEffect(() => {
    if (!attivo) return undefined;
    const oggi = adessoRoma().data;
    const off = [
      onSnapshot(query(collection(db, "formazioneFasce"), where("data", ">=", oggi)), s => setFasce(s.docs.map(d => ({ id: d.id, ...d.data() }))), () => setFasce([])),
      onSnapshot(doc(db, "formazioneAvvii", orgId), s => setAvvio(s.exists() ? s.data() : null), () => setAvvio(null)),
      onSnapshot(query(collection(db, "formazionePrenotazioni"), where("orgId", "==", orgId)), s => setPrenotazioni(s.docs.map(d => ({ id: d.id, ...d.data() }))), () => setPrenotazioni([])),
      onSnapshot(query(collection(db, "richiesteFormazione"), where("orgId", "==", orgId)), s => setRichieste(s.docs.map(d => ({ id: d.id, ...d.data() }))), () => setRichieste([])),
    ];
    return () => off.forEach(f => f());
  }, [attivo, orgId]);

  const periodoAvvio = avvioDisponibile(org);
  const avvioOk = periodoAvvio && !avvio;
  // l'avvio dell'azienda risulta già preso mentre la finestra è aperta: si passa al pacchetto
  useEffect(() => { if (formula === "avvio" && avvio && !esito) { setFormula("videocall"); setScelta(null); } }, [formula, avvio, esito]);

  const adesso = adessoRoma();
  const disponibili = useMemo(() => (fasce ? fasceDisponibili(fasce, formula, adessoRoma()) : []), [fasce, formula]);
  const precedenti = useMemo(() => (A_PAGAMENTO.includes(formula) ? precedentiAzienda({ prenotazioni, richieste, uid, oggi: adessoRoma().data }) : []), [formula, prenotazioni, richieste, uid]);
  const mie = prenotazioni.filter(p => p.stato !== "annullata" && p.data >= adesso.data).sort((a, b) => (a.data + a.inizio).localeCompare(b.data + b.inizio));

  if (!aperta) return null;
  const chiudi = () => setAperta(false);
  const pacchetto = formula === "videocall" || formula === "presenza";
  const caricando = attivo && fasce === null;
  const senzaDate = !caricando && disponibili.length === 0;
  const richiesta = modoRichiesta || senzaDate;
  const sedeOk = formula !== "presenza" || sede.trim().length >= 5;
  const valida = !anteprima && !!formula && sedeOk && (formula !== "avvio" || avvioOk) && (richiesta ? date.trim().length >= 3 : !!scelta);
  const datiComuni = () => ({ formula, partecipanti: Math.max(1, Math.min(50, Number(partecipanti) || 1)), oreAggiuntive: pacchetto ? Math.max(0, Math.min(20, Number(oreAggiuntive) || 0)) : 0,
    sede: formula === "presenza" ? sede.trim().slice(0, 200) : "", telefono: telefono.trim().slice(0, 40), note: note.trim().slice(0, 1000) });

  const invia = async () => {
    if (anteprima || !valida || invio) return;
    setInvio(true); setErrore("");
    try {
      if (!richiesta) {
        const r = await chiama("prenotaFormazione", { ...datiComuni(), fasciaId: scelta.fasciaId, inizio: scelta.inizio });
        const fascia = (fasce || []).find(f => f.id === scelta.fasciaId) || {};
        setEsito({ tipo: "prenotata", stato: r?.data?.stato || (formula === "avvio" ? "confermata" : "da_confermare"), data: fascia.data, inizio: scelta.inizio, fine: fineIncontro(scelta.inizio, formula) });
      } else if (formula === "avvio") {
        await chiama("prenotaFormazione", { ...datiComuni(), date: date.trim().slice(0, 300) });   // resta unico per azienda
        setEsito({ tipo: "richiesta" });
      } else {
        await addDoc(collection(db, "richiesteFormazione"), {
          orgId, orgNome: org?.name || org?.nome || "", uid, nome: userProfile?.displayName || currentUser?.displayName || "",
          email: currentUser?.email || "", ...datiComuni(), date: date.trim().slice(0, 300), utentiAttivi: org?.seatsUsed || 0, stato: "nuova", createdAt: serverTimestamp(),
        });
        setEsito({ tipo: "richiesta" });
      }
    } catch (e) {
      setErrore(messaggioDi(e));
      if (e?.details?.codice === "orario_occupato" || e?.details?.codice === "fascia") setScelta(null);
    }
    setInvio(false);
  };

  const annulla = async (p) => {
    const ok = await confirm(`Annullare la formazione di ${giornoEsteso(p.data)}, dalle ${p.inizio} alle ${p.fine}? L'orario torna libero${p.formula === "avvio" ? " e l'avvio gratuito si potrà prenotare di nuovo" : ""}.`, { title: "Annulla la formazione", type: "warning", confirmLabel: "Annulla la formazione" });
    if (!ok) return;
    try { await chiama("aggiornaPrenotazioneFormazione", { id: p.id, azione: "annulla" }); addToast("Formazione annullata: riceverai un'email di conferma.", "success"); }
    catch (e) { addToast(messaggioDi(e), "error", 8000); }
  };

  const lbl = { fontSize: 12, fontWeight: 700, color: "#374151", display: "block", marginBottom: 4 };
  const inp = { width: "100%", boxSizing: "border-box", padding: "8px 10px", border: "1px solid #d1d5db", borderRadius: 8, fontSize: 14, fontFamily: "inherit" };
  const scatola = (bg, bordo) => ({ background: bg, border: `1px solid ${bordo}`, borderRadius: 10, padding: "9px 11px", fontSize: 12.5, lineHeight: 1.55, marginBottom: 12 });
  const giorniVisti = tutteLeDate ? disponibili : disponibili.slice(0, 6);
  const testoSenzaDate = formula === "presenza"
    ? "Per la formazione in presenza al momento non ci sono date da prenotare."
    : "Al momento non ci sono date disponibili da prenotare.";

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && chiudi()} style={{ zIndex: 7200 }}>
      <div className="modal" role="dialog" aria-label="Richiedi formazione" style={{ maxWidth: 560 }}>
        <div style={{ display: "flex", alignItems: "center", marginBottom: 6 }}>
          <div style={{ flex: 1, fontSize: 17, fontWeight: 800, color: "#1e1b4b" }}>🎓 Formazione su Nexiora</div>
          <button type="button" className="btn-icon" onClick={chiudi} aria-label="Chiudi">✕</button>
        </div>
        {!puo ? (
          <div style={{ fontSize: 13.5, color: "#374151", lineHeight: 1.6 }}>La formazione la prenotano il titolare o un amministratore della tua azienda. Prezzi e programma: <a href="https://nexiora.it/formazione.html" target="_blank" rel="noreferrer" style={{ color: "#6d28d9", fontWeight: 700 }}>nexiora.it/formazione</a>.</div>
        ) : esito ? (
          <div style={{ fontSize: 14, color: "#374151", lineHeight: 1.6 }}>
            {esito.tipo === "prenotata" ? (<>
              <div style={{ fontSize: 30 }}>{esito.stato === "confermata" ? "✅" : "📅"}</div>
              <b>{esito.stato === "confermata" ? "Formazione prenotata" : "Data bloccata, in attesa di conferma"}: {giornoEsteso(esito.data)}, dalle {esito.inizio} alle {esito.fine}.</b>
              <div style={{ marginTop: 6 }}>{esito.stato === "confermata"
                ? "Riceverai un'email con l'invito da aggiungere al calendario e il link della video call."
                : `Ti scriviamo entro ${F.giorniRisposta} giorni lavorativi con la conferma e la fattura: la prenotazione è confermata solo dopo la nostra conferma.`}</div>
              <div style={{ marginTop: 6, fontSize: 12.5, color: "#6b7280" }}>Puoi spostarla o annullarla gratis fino a {F.oreSpostamentoGratuito} ore prima, da questa stessa finestra.</div>
            </>) : (<>
              <div style={{ fontSize: 30 }}>✅</div>
              <b>Richiesta inviata.</b> Vi rispondiamo entro {F.giorniRisposta} giorni lavorativi con le prime date disponibili. La prenotazione è confermata, e la fattura emessa, solo dopo l'accordo sulla data. Riceverete anche un'email di conferma.
            </>)}
            <div style={{ marginTop: 14 }}><button type="button" className="btn btn-primary" onClick={chiudi}>Chiudi</button></div>
          </div>
        ) : (<>
          {anteprima && <div role="note" aria-label="Anteprima" style={scatola("#eff6ff", "#bfdbfe")}><b style={{ color: "#1d4ed8" }}>👁️ Anteprima del Platform Owner.</b> Vedi gli orari e le prenotazioni come li vede adesso il titolare di {org?.name || org?.nome || "questa azienda"}, ma da qui non si prenota e non si annulla: le prenotazioni si gestiscono dalla Dashboard Nexiora → Formazione.</div>}
          {mie.length > 0 && (
            <div aria-label="Le vostre prenotazioni" style={{ ...scatola("#f5f3ff", "#ddd6fe"), fontSize: 13 }}>
              <div style={{ fontWeight: 800, color: "#4c1d95", marginBottom: 4 }}>Le vostre prenotazioni</div>
              {mie.map(p => {
                const st = STATI[p.stato] || STATI.da_confermare;
                const inTempo = minutiAlInizio(p.data, p.inizio, adesso) >= REGOLE_CALENDARIO.oreAnnullamento * 60;
                return (
                  <div key={p.id} style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", padding: "5px 0", borderTop: "1px solid #ede9fe" }}>
                    <span style={{ flex: "1 1 220px" }}>
                      <b>{giornoEsteso(p.data)}, {p.inizio}–{p.fine}</b> · {NOMI_FORMULE[p.formula]}{p.uid !== uid && p.nome ? ` · ${p.nome}` : ""}
                      <span style={{ marginLeft: 6, fontSize: 11, fontWeight: 700, color: st.c, background: st.bg, borderRadius: 6, padding: "1px 6px" }}>{st.t}</span>
                    </span>
                    {p.stato === "confermata" && p.link && <a href={p.link} target="_blank" rel="noreferrer" style={{ fontSize: 12.5, color: "#6d28d9", fontWeight: 700 }}>Entra nella video call</a>}
                    {anteprima ? null : inTempo
                      ? <button type="button" className="btn btn-secondary btn-sm" onClick={() => annulla(p)} style={{ fontSize: 12 }}>Annulla</button>
                      : <span style={{ fontSize: 11.5, color: "#6b7280" }}>Per annullare ora scrivi a info@nexiora.it</span>}
                  </div>
                );
              })}
              <div style={{ fontSize: 11.5, color: "#6b7280", marginTop: 4 }}>Per spostarla: annullala (fino a {F.oreSpostamentoGratuito} ore prima) e prenota un'altra data.</div>
            </div>
          )}
          <div style={{ fontSize: 12.5, color: "#6b7280", lineHeight: 1.55, marginBottom: 12 }}>Su prenotazione e secondo disponibilità. Non è un acquisto: per le formule a pagamento la data è confermata da noi e solo dopo arriva la fattura. Prezzi IVA esclusa.</div>
          <div role="radiogroup" aria-label="Formula" style={{ display: "grid", gap: 6, marginBottom: 12 }}>
            {FORMULE.map(f => {
              const off = f.id === "avvio" && !avvioOk;
              const motivo = f.id === "avvio" && avvio ? testoAvvioUsato(avvio) : `Disponibile durante la prova, entro i primi ${F.avvioEntroGiorniProva} giorni.`;
              return (
                <label key={f.id} style={{ display: "flex", gap: 10, padding: "8px 10px", borderRadius: 9, cursor: off ? "default" : "pointer", opacity: off ? .6 : 1,
                  border: `1.5px solid ${formula === f.id ? "#7c3aed" : "#e5e7eb"}`, background: formula === f.id ? "#f5f3ff" : "#fff" }}>
                  <input type="radio" name="formulaFormazione" checked={formula === f.id} disabled={off} onChange={() => { setFormula(f.id); setScelta(null); setModoRichiesta(false); setErrore(""); }} style={{ marginTop: 3 }} />
                  <span style={{ flex: 1 }}>
                    <span style={{ display: "flex", gap: 8, fontSize: 13.5, fontWeight: 700, color: "#1e1b4b" }}><span style={{ flex: 1 }}>{f.label}</span><span style={{ color: "#6d28d9", whiteSpace: "nowrap" }}>{f.prezzo}</span></span>
                    <span style={{ display: "block", fontSize: 12, color: off && avvio ? "#92400e" : "#6b7280", marginTop: 2 }}>{off ? motivo : f.nota}</span>
                  </span>
                </label>
              );
            })}
          </div>

          {precedenti.length > 0 && (
            <div role="note" aria-label="Un collega ha già fatto la formazione" style={scatola("#fffbeb", "#fde68a")}>
              <b style={{ color: "#92400e" }}>⚠️ Un collega ha già fatto la formazione</b>
              {precedenti.slice(0, 3).map((p, i) => <div key={i} style={{ color: "#78350f" }}>{testoPrecedente(p)}</div>)}
              <div style={{ color: "#78350f", marginTop: 2 }}>Puoi prenotare comunque, ad esempio per altre persone o altri argomenti.</div>
            </div>
          )}

          {caricando ? <div style={{ fontSize: 12.5, color: "#9ca3af", marginBottom: 12 }}>Carico le date disponibili…</div>
            : !richiesta ? (
              <div style={{ marginBottom: 12 }}>
                <div style={lbl}>Scegli giorno e orario ({formula === "avvio" ? `${REGOLE_CALENDARIO.avvioMinuti} minuti` : formula === "ora" ? "1 ora" : "3 ore"})</div>
                <div style={{ display: "grid", gap: 8 }}>
                  {giorniVisti.map(({ fascia, orari }) => (
                    <div key={fascia.id} style={{ border: "1px solid #e5e7eb", borderRadius: 9, padding: "7px 9px" }}>
                      <div style={{ fontSize: 12.5, fontWeight: 700, color: "#1e1b4b", marginBottom: 5, textTransform: "capitalize" }}>{giornoEsteso(fascia.data)}</div>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                        {orari.map(o => {
                          const su = scelta?.fasciaId === fascia.id && scelta?.inizio === o;
                          return (
                            <button key={o} type="button" aria-pressed={su} onClick={() => { setScelta({ fasciaId: fascia.id, inizio: o }); setErrore(""); }}
                              style={{ padding: "5px 10px", borderRadius: 8, fontSize: 12.5, fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
                                border: `1.5px solid ${su ? "#7c3aed" : "#ddd6fe"}`, background: su ? "#7c3aed" : "#fff", color: su ? "#fff" : "#5b21b6" }}>
                              {o}–{fineIncontro(o, formula)}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
                {disponibili.length > giorniVisti.length && <button type="button" onClick={() => setTutteLeDate(true)} style={{ marginTop: 6, background: "none", border: "none", color: "#6d28d9", fontWeight: 700, fontSize: 12.5, cursor: "pointer", padding: 0 }}>Mostra altre {disponibili.length - giorniVisti.length} date</button>}
                <div style={{ marginTop: 8 }}>
                  <button type="button" onClick={() => { setModoRichiesta(true); setScelta(null); }} style={{ background: "none", border: "none", color: "#6d28d9", fontWeight: 700, fontSize: 12.5, cursor: "pointer", padding: 0, textDecoration: "underline" }}>
                    Nessuna data va bene? Lasciaci una richiesta
                  </button>
                </div>
              </div>
            ) : (
              <div style={scatola("#f9fafb", "#e5e7eb")}>
                {senzaDate ? <><b>{testoSenzaDate}</b> Lasciaci la tua richiesta: ti rispondiamo entro {F.giorniRisposta} giorni lavorativi con le date possibili.</>
                  : <>Indicaci le date che preferisci: ti rispondiamo entro {F.giorniRisposta} giorni lavorativi. <button type="button" onClick={() => setModoRichiesta(false)} style={{ background: "none", border: "none", color: "#6d28d9", fontWeight: 700, fontSize: 12.5, cursor: "pointer", padding: 0 }}>← Torna alle date disponibili</button></>}
              </div>
            )}

          {pacchetto && (
            <div style={{ marginBottom: 10 }}>
              <label style={lbl} htmlFor="fz-ore">Ore aggiuntive al pacchetto ({euro(F.oraAggiuntiva)} l'ora, entro {F.mesiOreAggiuntive} mesi)</label>
              <input id="fz-ore" type="number" min="0" max="20" value={oreAggiuntive} onChange={e => setOreAggiuntive(e.target.value)} style={{ ...inp, width: 100 }} />
            </div>
          )}
          {formula === "presenza" && (
            <div style={{ marginBottom: 10 }}>
              <label style={lbl} htmlFor="fz-sede">Indirizzo della sede *</label>
              <input id="fz-sede" value={sede} onChange={e => setSede(e.target.value)} style={inp} placeholder="Via, numero, comune" />
            </div>
          )}
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 10 }}>
            <div style={{ flex: "1 1 120px" }}>
              <label style={lbl} htmlFor="fz-part">Partecipanti</label>
              <input id="fz-part" type="number" min="1" max="50" value={partecipanti} onChange={e => setPartecipanti(e.target.value)} style={inp} />
            </div>
            <div style={{ flex: "2 1 200px" }}>
              <label style={lbl} htmlFor="fz-tel">Telefono</label>
              <input id="fz-tel" value={telefono} onChange={e => setTelefono(e.target.value)} style={inp} placeholder="Per qualunque necessità" />
            </div>
          </div>
          {Number(partecipanti) > F.maxPartecipanti && <div style={{ fontSize: 12, color: "#92400e", marginBottom: 8 }}>Oltre {F.maxPartecipanti} partecipanti prepariamo un preventivo dedicato.</div>}
          {richiesta && (
            <div style={{ marginBottom: 10 }}>
              <label style={lbl} htmlFor="fz-date">Date e orari possibili *</label>
              <input id="fz-date" value={date} onChange={e => setDate(e.target.value)} style={inp} placeholder="Es. martedì o giovedì mattina delle prossime due settimane" />
            </div>
          )}
          <div style={{ marginBottom: 12 }}>
            <label style={lbl} htmlFor="fz-note">Su cosa volete concentrarvi?</label>
            <textarea id="fz-note" value={note} onChange={e => setNote(e.target.value)} rows={2} style={{ ...inp, resize: "vertical" }} placeholder="Es. presenze e controllo economico; SAL; formare i capisquadra sui rapportini" />
          </div>
          {errore && <div role="alert" style={{ ...scatola("#fef2f2", "#fecaca"), color: "#991b1b" }}>{errore}</div>}
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", flexWrap: "wrap" }}>
            <a href="https://nexiora.it/formazione.html" target="_blank" rel="noreferrer" style={{ fontSize: 12.5, color: "#6d28d9", fontWeight: 700, alignSelf: "center", marginRight: "auto" }}>Programma e prezzi →</a>
            <button type="button" className="btn btn-secondary" onClick={chiudi}>Chiudi</button>
            <button type="button" className="btn btn-primary" onClick={invia} disabled={!valida || invio || caricando}>
              {anteprima ? "Anteprima: da Platform Owner non si prenota" : invio ? "Invio…" : richiesta ? "Invia la richiesta" : formula === "avvio" ? "Prenota" : "Prenota (da confermare)"}
            </button>
          </div>
        </>)}
      </div>
    </div>
  );
}
